/**
 * Resources — Consommation des ressources (Cluster / Projets / Tokens LLM)
 * Tabs: Cluster (CPU/mémoire/disk par nœud), Projets (table de consommation),
 *       LLM tokens (usage par provider)
 * Données placeholder acceptables.
 */

import { useState, useEffect } from "react";
import { api } from "../lib/api";

type Tab = "cluster" | "projects" | "llm";

interface NodeResource {
  name: string;
  role: "master" | "worker";
  status: "Ready" | "NotReady";
  cpu: number;
  cpuCores: number;
  memory: number;
  memoryTotal: string;
  disk: number;
  diskTotal: string;
}

interface ProjectResource {
  name: string;
  cpu: string;
  memory: string;
  pods: number;
  cost: number;
  env: string;
}

interface LlmUsage {
  provider: string;
  model: string;
  icon: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cost: number;
  budget: number;
}

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: "cluster", label: "Cluster", icon: "🖥" },
  { key: "projects", label: "Projets", icon: "📦" },
  { key: "llm", label: "LLM tokens", icon: "🧠" },
];

function pctColor(pct: number): string {
  if (pct > 85) return "var(--rust)";
  if (pct > 65) return "var(--amber)";
  return "var(--olive)";
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

export default function Resources() {
  const [tab, setTab] = useState<Tab>("cluster");
  const [nodes, setNodes] = useState<NodeResource[]>([]);
  const [projects, setProjects] = useState<ProjectResource[]>([]);
  const [llm, setLlm] = useState<LlmUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.resources.get();
      setNodes((data.nodes ?? []) as NodeResource[]);
      setProjects((data.projects ?? []) as ProjectResource[]);
      setLlm((data.llm ?? []) as LlmUsage[]);
      // LIV-02: état honnête — pas de données fabriquées quand indisponible.
      setError(data.available ? null : (data.detail ?? "Métriques de ressources non disponibles."));
    } catch (err) {
      setNodes([]);
      setProjects([]);
      setLlm([]);
      setError(err instanceof Error ? err.message : "Ressources injoignables.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Cluster stats
  const totalCores = nodes.reduce((a, n) => a + n.cpuCores, 0);
  const avgCpu = nodes.length > 0 ? Math.round(nodes.reduce((a, n) => a + n.cpu, 0) / nodes.length) : 0;
  const avgMem = nodes.length > 0 ? Math.round(nodes.reduce((a, n) => a + n.memory, 0) / nodes.length) : 0;
  const avgDisk = nodes.length > 0 ? Math.round(nodes.reduce((a, n) => a + n.disk, 0) / nodes.length) : 0;

  // Project stats
  const totalProjectCost = projects.reduce((a, p) => a + p.cost, 0);
  const totalPods = projects.reduce((a, p) => a + p.pods, 0);

  // LLM stats
  const totalTokens = llm.reduce((a, l) => a + l.totalTokens, 0);
  const totalLlmCost = llm.reduce((a, l) => a + l.cost, 0);
  const totalBudget = llm.reduce((a, l) => a + l.budget, 0);

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">utilisation</div>
          <h1>Ressources</h1>
          <p>Consommation cluster, projets et tokens LLM</p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load}>↻ Actualiser</button>
        </div>
      </div>

      {error && (
        <div className="ops-card" style={{ borderColor: "var(--amber)" }}>
          <p style={{ color: "var(--amber)" }}>⚠ {error}</p>
        </div>
      )}

      {loading && <div className="no-results">Chargement…</div>}

      {/* Tabs */}
      {!loading && (
        <div className="view-tabs">
          {TABS.map((t) => (
            <span
              key={t.key}
              className={`view-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </span>
          ))}
        </div>
      )}

      {/* ── Cluster tab ── */}
      {!loading && tab === "cluster" && (
        <>
          <div className="stats-row" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <div className="stat-card">
              <div className="eyebrow">nœuds</div>
              <div className="val">{nodes.length}</div>
              <div className="sub">{totalCores} cœurs</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">cpu moyen</div>
              <div className="val" style={{ color: pctColor(avgCpu) }}>{avgCpu}%</div>
              <div className="sub">utilisation</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">mémoire moy.</div>
              <div className="val" style={{ color: pctColor(avgMem) }}>{avgMem}%</div>
              <div className="sub">utilisation</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">disque moy.</div>
              <div className="val" style={{ color: pctColor(avgDisk) }}>{avgDisk}%</div>
              <div className="sub">utilisation</div>
            </div>
          </div>

          <div className="health-grid">
            {nodes.map((node) => (
              <div key={node.name} className="health-card">
                <div className="health-head">
                  <span className="dot-sm" style={{ width: "8px", height: "8px", borderRadius: "999px", background: node.status === "Ready" ? "var(--olive)" : "var(--rust)" }} />
                  <span className="health-name">{node.name}</span>
                  <span className={`cluster-node-role ${node.role === "worker" ? "worker" : ""}`}>{node.role}</span>
                </div>
                <ResourceBar label="CPU" pct={node.cpu} detail={`${node.cpuCores} cœurs`} />
                <ResourceBar label="Mémoire" pct={node.memory} detail={node.memoryTotal} />
                <ResourceBar label="Disque" pct={node.disk} detail={node.diskTotal} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Projects tab ── */}
      {!loading && tab === "projects" && (
        <>
          <div className="stats-row" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <div className="stat-card">
              <div className="eyebrow">projets</div>
              <div className="val">{projects.length}</div>
              <div className="sub">actifs</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">pods</div>
              <div className="val">{totalPods}</div>
              <div className="sub">au total</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">coût mensuel</div>
              <div className="val" style={{ color: "var(--purple)" }}>{totalProjectCost.toFixed(0)}€</div>
              <div className="sub">estimé</div>
            </div>
          </div>

          <div className="ops-card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Projet</th>
                  <th>Env</th>
                  <th>CPU</th>
                  <th>Mémoire</th>
                  <th>Pods</th>
                  <th>Coût/mois</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.name}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>
                      <span className={`env-badge ${p.env}`}>{p.env}</span>
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: "11px" }}>{p.cpu}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: "11px" }}>{p.memory}</td>
                    <td>{p.pods}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--purple)" }}>{p.cost.toFixed(1)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── LLM tab ── */}
      {!loading && tab === "llm" && (
        <>
          <div className="stats-row" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <div className="stat-card">
              <div className="eyebrow">tokens total</div>
              <div className="val" style={{ color: "var(--purple)" }}>{fmtTokens(totalTokens)}</div>
              <div className="sub">ce mois</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">coût LLM</div>
              <div className="val" style={{ color: "var(--purple)" }}>{totalLlmCost.toFixed(1)}€</div>
              <div className="sub">ce mois</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">budget</div>
              <div className="val" style={{ color: "var(--blue)" }}>{totalBudget.toFixed(0)}€</div>
              <div className="sub">alloué</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">utilisation</div>
              <div className="val" style={{ color: totalLlmCost / totalBudget > 0.8 ? "var(--rust)" : "var(--olive)" }}>
                {Math.round((totalLlmCost / totalBudget) * 100)}%
              </div>
              <div className="sub">du budget</div>
            </div>
          </div>

          <div style={{ display: "grid", gap: "12px" }}>
            {llm.map((l) => {
              const budgetPct = Math.round((l.cost / l.budget) * 100);
              return (
                <div key={`${l.provider}-${l.model}`} className="cost-provider-row">
                  <div className="cost-provider-icon" style={{ background: "rgba(183,148,246,0.12)" }}>
                    {l.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "13px" }}>{l.provider} · {l.model}</div>
                    <div style={{ fontSize: "11px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
                      prompt: {fmtTokens(l.promptTokens)} · completion: {fmtTokens(l.completionTokens)}
                    </div>
                  </div>
                  <div style={{ minWidth: "160px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "4px" }}>
                      <span style={{ color: "var(--muted)" }}>{l.cost.toFixed(1)}€ / {l.budget}€</span>
                      <span style={{ color: budgetPct > 80 ? "var(--rust)" : "var(--muted)" }}>{budgetPct}%</span>
                    </div>
                    <div className="budget-bar">
                      <div
                        className="budget-fill"
                        style={{
                          width: `${Math.min(budgetPct, 100)}%`,
                          background: budgetPct > 80 ? "var(--rust)" : budgetPct > 60 ? "var(--amber)" : "var(--olive)",
                        }}
                      />
                    </div>
                  </div>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "var(--purple)", fontWeight: 600 }}>
                    {fmtTokens(l.totalTokens)}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Resource Bar component ───────────────────────────────────────

function ResourceBar({ label, pct, detail }: { label: string; pct: number; detail: string }) {
  return (
    <div style={{ display: "grid", gap: "4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px" }}>
        <span style={{ fontFamily: "var(--mono)", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
        <span style={{ fontFamily: "var(--mono)", color: pctColor(pct) }}>{pct}%</span>
      </div>
      <div className="satisfaction-bar">
        <div
          className="satisfaction-fill"
          style={{
            width: `${pct}%`,
            background: pctColor(pct),
          }}
        />
      </div>
      <div style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)" }}>{detail}</div>
    </div>
  );
}
